import { FormEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Loader2, ShieldCheck } from "lucide-react";
import { SiteNav } from "@/components/site/SiteNav";
import { SiteFooter } from "@/components/site/SiteFooter";
import { PageMeta } from "@/components/site/PageMeta";
import { BUILT_INDEX_ROUTES } from "@/content/built-index";
import { startBuiltIndex } from "@/lib/built-index-session";
import { trackBuiltIndexStep } from "@/lib/built-index-analytics";
import { syncLeadToCrm } from "@/lib/crm-sync";


const field =
  "w-full bg-white/[0.04] border border-white/15 text-white px-4 py-3 text-sm placeholder:text-silver/50 focus:outline-none focus:border-gold";

const BuiltIndexStart = () => {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    trackBuiltIndexStep("lead_gate_view");
  }, []);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (busy) return;
    const form = new FormData(e.currentTarget);
    // Honeypot: bots fill hidden fields, humans never see this one.
    if (form.get("company")) return;

    setBusy(true);
    setError(null);
    try {
      const email = String(form.get("email") ?? "");
      const role = String(form.get("role") ?? "") || undefined;
      const sessionId = await startBuiltIndex({
        fullName: String(form.get("fullName") ?? ""),
        email,
        phone: String(form.get("phone") ?? "") || undefined,
        role,
      });
      trackBuiltIndexStep("lead_gate_submit", { sessionId, email, metadata: { role } });
      syncLeadToCrm({
        event: "built_index_lead",
        email,
        fullName: String(form.get("fullName") ?? ""),
        phone: String(form.get("phone") ?? "") || null,
        role,
        source: "built_index_lead_gate",
      });

      navigate(BUILT_INDEX_ROUTES.built);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong.");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <PageMeta
        title="Start The Built Index™ Assessment · Dr. Sully Speaks"
        description="Begin your free leadership diagnostic. Four assessments, about twelve minutes, your score delivered instantly."
        path="/built-index/start"
      />
      <SiteNav />

      <section className="relative pt-32 pb-20 md:pt-40 bg-hero">
        <div
          aria-hidden
          className="absolute inset-0 bg-[radial-gradient(circle_at_25%_25%,hsl(var(--gold)/0.1),transparent_55%)]"
        />
        <div className="container relative max-w-xl">
          <p className="text-[10px] uppercase tracking-[0.3em] text-gold">
            The Built Index™
          </p>
          <h1 className="font-display font-bold uppercase text-white text-3xl md:text-5xl mt-3 leading-[1.05]">
            Where should we send your score?
          </h1>
          <p className="text-silver mt-4 leading-relaxed">
            Four short assessments, roughly twelve minutes. Your results are generated
            the moment you finish.
          </p>

          <form onSubmit={onSubmit} noValidate className="mt-8 space-y-4">
            <div>
              <label htmlFor="fullName" className="block text-xs uppercase tracking-[0.2em] text-silver mb-2">
                Full name
              </label>
              <input id="fullName" name="fullName" required autoComplete="name" maxLength={120} className={field} />
            </div>
            <div>
              <label htmlFor="email" className="block text-xs uppercase tracking-[0.2em] text-silver mb-2">
                Email
              </label>
              <input id="email" name="email" type="email" required autoComplete="email" maxLength={254} className={field} />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="role" className="block text-xs uppercase tracking-[0.2em] text-silver mb-2">
                  Role <span className="text-silver/50">(optional)</span>
                </label>
                <input id="role" name="role" maxLength={120} className={field} />
              </div>
              <div>
                <label htmlFor="phone" className="block text-xs uppercase tracking-[0.2em] text-silver mb-2">
                  Phone <span className="text-silver/50">(optional)</span>
                </label>
                <input id="phone" name="phone" type="tel" autoComplete="tel" maxLength={40} className={field} />
              </div>
            </div>

            <input
              type="text"
              name="company"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              className="hidden"
            />

            {error && (
              <p role="alert" className="text-sm text-destructive-foreground bg-destructive/20 border border-destructive/40 px-4 py-3">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={busy}
              className="inline-flex w-full items-center justify-center gap-2 bg-orange text-primary-foreground px-6 py-3.5 text-xs uppercase tracking-[0.2em] font-semibold hover:brightness-110 transition disabled:opacity-60"
            >
              {busy ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Starting…
                </>
              ) : (
                <>
                  Begin Assessment <ArrowRight className="h-4 w-4" />
                </>
              )}
            </button>

            <p className="flex items-center gap-2 text-silver/60 text-[11px]">
              <ShieldCheck className="h-3.5 w-3.5 text-gold" />
              Your answers stay private. No spam, no list rental.
            </p>
          </form>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default BuiltIndexStart;
